import React, { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Autocomplete,
  Avatar,
  Box,
  Button,
  Checkbox,
  Container,
  IconButton,
  LinearProgress,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableFooter,
  TableHead,
  TableRow,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  ThemeProvider,
  Typography,
  createTheme,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import DownloadIcon from '@mui/icons-material/Download';
import ViewWeekIcon from '@mui/icons-material/ViewWeek';
import dayjs from 'dayjs';
import * as XLSX from 'xlsx';
import {
  fetchCustomerOptions,
  getCustomerOrderMatrixResult,
  getCustomerOrderMatrixStatus,
  prepareCustomerOrderMatrix,
} from '../api/customerOrderMatrix';
import { pageColors } from './HomePage';

const POLL_INTERVAL_MS = 2000;
const CUSTOMER_OPTIONS_TIMEOUT_MS = 15000;
const CUSTOMER_OPTIONS_TIMEOUT_MESSAGE =
  'Loading customers took too long. Open the picker again to retry.';
const CUSTOMER_OPTIONS_ERROR_MESSAGE =
  'Failed to load customer options. Try reopening the picker.';
const PREPARE_ERROR_MESSAGE = 'Failed to prepare the customer report. Try again.';
const STATUS_ERROR_MESSAGE = 'Failed to load the customer report status. Try again.';
const RESULT_ERROR_MESSAGE = 'Failed to load the customer report. Try again.';
const GENERATION_ERROR_MESSAGE = 'Unable to generate the customer report. Try again.';
const EXPIRED_RESULT_MESSAGE = 'The report expired or was not prepared. Generate it again.';
const CUSTOM_RANGE_WARNING_MESSAGE =
  'Selected date range is longer than 31 days. This report may take longer to generate.';
const MAX_VISIBLE_CUSTOMER_OPTIONS = 100;
const DESCRIPTION_COLUMN_WIDTH = 228;
const UNIT_COLUMN_WIDTH = 80;
const CUSTOMER_COLUMN_WIDTH = 124;
const TOTAL_QUANTITY_COLUMN_WIDTH = 120;
const REVENUE_COLUMN_WIDTH = 120;

const CUSTOMER_MATRIX_THEME = createTheme({
  palette: {
    primary: {
      main: pageColors.customerOrderMatrix,
    },
  },
  typography: {
    fontFamily: 'Inter, Roboto, sans-serif',
  },
});

const PAGE_BACKGROUND =
  'radial-gradient(circle at top left, rgba(84,110,122,0.18), transparent 55%), #f5f7fb';

const SURFACE_SX = {
  borderRadius: 4,
  backgroundColor: '#fff',
  boxShadow: '0 18px 40px rgba(0,0,0,0.07)',
  border: '1px solid rgba(0,0,0,0.05)',
};

const MONTH_FILTER_FIELD_SX = {
  width: { xs: '100%', sm: 180 },
  flex: { sm: '0 0 auto' },
};

const DATE_FILTER_FIELD_SX = {
  width: { xs: '100%', sm: 190 },
  flex: { sm: '0 0 auto' },
};

const PERIOD_INPUT_SX = {
  '& .MuiInputBase-root': {
    height: 40,
  },
};

const CUSTOMER_FILTER_FIELD_SX = {
  width: { xs: '100%', sm: 320 },
  flex: { sm: '0 0 auto' },
};

const TABLE_HEADER_SX = {
  backgroundColor: '#fafafa',
  whiteSpace: 'nowrap',
  fontWeight: 700,
  top: 0,
  zIndex: 3,
};

const TABLE_EDGE_STICKY_HEADER_SX = {
  ...TABLE_HEADER_SX,
  zIndex: 4,
};

const TABLE_ROW_HOVER_SX = {
  '&:hover': {
    backgroundColor: 'rgba(0,0,0,0.03)',
  },
};

const QUANTITY_FORMATTER = new Intl.NumberFormat('sv-SE', {
  minimumFractionDigits: 0,
  maximumFractionDigits: 3,
});

const REVENUE_FORMATTER = new Intl.NumberFormat('sv-SE', {
  style: 'currency',
  currency: 'SEK',
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

function normalizeMonthValue(value) {
  if (!value) {
    return null;
  }

  const parsed = dayjs(value);
  return parsed.isValid() ? parsed.startOf('month') : null;
}

function normalizeDateValue(value) {
  if (!value) {
    return null;
  }

  const parsed = dayjs(value);
  return parsed.isValid() ? parsed.startOf('day') : null;
}

function normalizeCustomerSelections(customers) {
  const seenCustomerNumbers = new Set();

  return (Array.isArray(customers) ? customers : []).reduce((normalizedCustomers, customer) => {
    const customerNumber = String(customer?.customerNumber ?? '').trim();
    if (!customerNumber || seenCustomerNumbers.has(customerNumber)) {
      return normalizedCustomers;
    }

    seenCustomerNumbers.add(customerNumber);
    normalizedCustomers.push({
      customerNumber,
      customerName: String(customer?.customerName ?? '').trim() || customerNumber,
    });
    return normalizedCustomers;
  }, []);
}

function getCustomerDetailsLabel(customer) {
  return `${customer.customerName} (${customer.customerNumber})`;
}

function normalizeSearchValue(value) {
  return String(value ?? '').trim().toLowerCase();
}

function toFiniteNumber(value) {
  const numericValue = Number(value);
  return Number.isFinite(numericValue) ? numericValue : 0;
}

function formatQuantityValue(value) {
  const numericValue = toFiniteNumber(value);
  if (Object.is(numericValue, -0) || numericValue === 0) {
    return '0';
  }

  return QUANTITY_FORMATTER.format(numericValue);
}

function formatRevenueValue(value) {
  return REVENUE_FORMATTER.format(toFiniteNumber(value));
}

function buildPeriodSelection({ mode, month = '', startDate = '', endDate = '' }) {
  if (mode === 'custom') {
    return {
      mode: 'custom',
      month: '',
      startDate,
      endDate,
    };
  }

  return {
    mode: 'month',
    month,
    startDate: '',
    endDate: '',
  };
}

function buildPeriodSignature(period) {
  if (period.mode === 'custom') {
    return period.startDate && period.endDate
      ? `range:${period.startDate}:${period.endDate}`
      : '';
  }

  return period.month ? `month:${period.month}` : '';
}

function buildRequestSignature(period, customerNumbers) {
  const periodSignature = buildPeriodSignature(period);

  return periodSignature && customerNumbers.length > 0
    ? `${periodSignature}|${customerNumbers.join(',')}`
    : '';
}

function getInclusiveRangeLength(startDate, endDate) {
  const normalizedStartDate = normalizeDateValue(startDate);
  const normalizedEndDate = normalizeDateValue(endDate);

  if (
    !normalizedStartDate ||
    !normalizedEndDate ||
    normalizedStartDate.isAfter(normalizedEndDate, 'day')
  ) {
    return null;
  }

  return normalizedEndDate.diff(normalizedStartDate, 'day') + 1;
}

function getReportPeriod(reportData) {
  const month = String(reportData?.month ?? '').trim();
  const startDate = String(reportData?.startDate ?? '').trim();
  const endDate = String(reportData?.endDate ?? '').trim();

  return buildPeriodSelection({
    mode: month ? 'month' : 'custom',
    month,
    startDate,
    endDate,
  });
}

function getExportTitle(period) {
  if (period.month) {
    return `Customer Matrix ${period.month}`;
  }

  if (period.startDate && period.endDate) {
    return `Customer Matrix ${period.startDate} to ${period.endDate}`;
  }

  return 'Customer Matrix';
}

function getExportFilenameSuffix(period) {
  if (period.month) {
    return period.month;
  }

  if (period.startDate && period.endDate) {
    return `${period.startDate}_to_${period.endDate}`;
  }

  return 'report';
}

function isAbortError(error) {
  return error?.name === 'AbortError';
}

function getLeftStickyCellSx(left, width, backgroundColor, extraSx = {}) {
  return {
    position: 'sticky',
    left,
    width,
    minWidth: width,
    maxWidth: width,
    zIndex: 2,
    backgroundColor,
    borderRight: '1px solid rgba(0,0,0,0.08)',
    boxSizing: 'border-box',
    ...extraSx,
  };
}

function getRightStickyCellSx(right, width, backgroundColor, extraSx = {}) {
  return {
    position: 'sticky',
    right,
    width,
    minWidth: width,
    maxWidth: width,
    zIndex: 2,
    backgroundColor,
    borderLeft: '1px solid rgba(0,0,0,0.08)',
    boxSizing: 'border-box',
    ...extraSx,
  };
}

function preventDefaultMouseDown(event) {
  event.preventDefault();
}

function getSafeMatrixErrorMessage(phase) {
  switch (phase) {
    case 'customerOptions':
      return CUSTOMER_OPTIONS_ERROR_MESSAGE;
    case 'prepare':
      return PREPARE_ERROR_MESSAGE;
    case 'status':
      return STATUS_ERROR_MESSAGE;
    case 'result':
      return RESULT_ERROR_MESSAGE;
    case 'generation':
      return GENERATION_ERROR_MESSAGE;
    default:
      return GENERATION_ERROR_MESSAGE;
  }
}

const CustomerOrderMatrixTable = memo(function CustomerOrderMatrixTable({
  customerColumns,
  rows,
  grandTotalSalesDisplay,
  hasLoadedReport,
  hasSelectedCustomers,
  hasReadyPeriodSelection,
  isRefreshing,
  onRemoveCustomer,
}) {
  const columnCount = customerColumns.length + 4;
  const tableMinWidth =
    DESCRIPTION_COLUMN_WIDTH +
    UNIT_COLUMN_WIDTH +
    customerColumns.length * CUSTOMER_COLUMN_WIDTH +
    TOTAL_QUANTITY_COLUMN_WIDTH +
    REVENUE_COLUMN_WIDTH;

  const renderedRows = useMemo(() => {
    if (rows.length === 0) {
      return (
        <TableRow>
          <TableCell colSpan={columnCount} align="center" sx={{ py: 5 }}>
            {hasLoadedReport
              ? 'No article rows were returned for the selected customers and month or custom date range.'
              : isRefreshing
                ? 'Updating report...'
                : hasSelectedCustomers && hasReadyPeriodSelection
                  ? 'The selected customers define the report columns. Press Generate to load the report.'
                  : 'Pick a month or custom date range and customers to populate the report.'}
          </TableCell>
        </TableRow>
      );
    }

    return rows.map((row) => (
      <TableRow key={row.key} hover sx={TABLE_ROW_HOVER_SX}>
        <TableCell sx={getLeftStickyCellSx(0, DESCRIPTION_COLUMN_WIDTH, '#fff')}>
          <Box sx={{ minWidth: 0 }}>
            <Typography variant="body2" fontWeight={600} sx={{ overflowWrap: 'anywhere' }}>
              {row.articleDescriptionDisplay}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {row.articleNumberDisplay}
            </Typography>
          </Box>
        </TableCell>
        <TableCell sx={getLeftStickyCellSx(DESCRIPTION_COLUMN_WIDTH, UNIT_COLUMN_WIDTH, '#fff')}>
          {row.unitDisplay}
        </TableCell>
        {row.customerCells.map((cell) => (
          <TableCell
            key={cell.customerNumber}
            align="center"
            sx={{
              width: CUSTOMER_COLUMN_WIDTH,
              minWidth: CUSTOMER_COLUMN_WIDTH,
              maxWidth: CUSTOMER_COLUMN_WIDTH,
              whiteSpace: 'nowrap',
            }}
          >
            {cell.displayValue}
          </TableCell>
        ))}
        <TableCell
          align="center"
          sx={getRightStickyCellSx(
            REVENUE_COLUMN_WIDTH,
            TOTAL_QUANTITY_COLUMN_WIDTH,
            '#fff'
          )}
        >
          {row.totalQuantityDisplay}
        </TableCell>
        <TableCell
          align="center"
          sx={getRightStickyCellSx(0, REVENUE_COLUMN_WIDTH, '#fff')}
        >
          {row.totalSalesDisplay}
        </TableCell>
      </TableRow>
    ));
  }, [
    columnCount,
    hasLoadedReport,
    hasReadyPeriodSelection,
    hasSelectedCustomers,
    isRefreshing,
    rows,
  ]);

  return (
    <TableContainer
      component={Paper}
      elevation={0}
      sx={{
        ...SURFACE_SX,
        overflowX: 'auto',
        overflowY: 'auto',
        maxHeight: '72vh',
      }}
      data-testid="customer-matrix-table"
    >
      <Table
        stickyHeader
        aria-label="customer report table"
        sx={{
          minWidth: tableMinWidth,
          tableLayout: 'fixed',
        }}
      >
        <TableHead>
          <TableRow>
            <TableCell
              sx={getLeftStickyCellSx(
                0,
                DESCRIPTION_COLUMN_WIDTH,
                '#fafafa',
                TABLE_EDGE_STICKY_HEADER_SX
              )}
            >
              Benämning
            </TableCell>
            <TableCell
              sx={getLeftStickyCellSx(
                DESCRIPTION_COLUMN_WIDTH,
                UNIT_COLUMN_WIDTH,
                '#fafafa',
                TABLE_EDGE_STICKY_HEADER_SX
              )}
            >
              Enhet
            </TableCell>
            {customerColumns.map((customer) => (
              <TableCell
                key={customer.customerNumber}
                align="center"
                aria-label={getCustomerDetailsLabel(customer)}
                sx={{
                  ...TABLE_HEADER_SX,
                  width: CUSTOMER_COLUMN_WIDTH,
                  minWidth: CUSTOMER_COLUMN_WIDTH,
                  maxWidth: CUSTOMER_COLUMN_WIDTH,
                  px: 0.75,
                  verticalAlign: 'top',
                }}
              >
                <Stack spacing={0.25} sx={{ minWidth: 0, alignItems: 'stretch' }}>
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'flex-start',
                      justifyContent: 'space-between',
                      gap: 0.25,
                      minWidth: 0,
                    }}
                    title={getCustomerDetailsLabel(customer)}
                  >
                    <Typography
                      variant="caption"
                      fontWeight={700}
                      sx={{
                        display: 'block',
                        lineHeight: 1.15,
                        whiteSpace: 'normal',
                        overflowWrap: 'anywhere',
                        textAlign: 'left',
                        flex: 1,
                      }}
                    >
                      {customer.customerName}
                    </Typography>
                    <IconButton
                      size="small"
                      onClick={() => onRemoveCustomer(customer.customerNumber)}
                      aria-label={`Remove ${customer.customerName} from report`}
                      sx={{
                        ml: 0.25,
                        p: 0.25,
                        alignSelf: 'flex-start',
                      }}
                    >
                      <CloseIcon sx={{ fontSize: 14 }} />
                    </IconButton>
                  </Box>
                </Stack>
              </TableCell>
            ))}
            <TableCell
              align="center"
              sx={getRightStickyCellSx(
                REVENUE_COLUMN_WIDTH,
                TOTAL_QUANTITY_COLUMN_WIDTH,
                '#fafafa',
                TABLE_EDGE_STICKY_HEADER_SX
              )}
            >
              TOTAL ST/KG
            </TableCell>
            <TableCell
              align="center"
              sx={getRightStickyCellSx(
                0,
                REVENUE_COLUMN_WIDTH,
                '#fafafa',
                TABLE_EDGE_STICKY_HEADER_SX
              )}
            >
              Omsättning
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>{renderedRows}</TableBody>
        <TableFooter>
          <TableRow>
            <TableCell
              sx={getLeftStickyCellSx(0, DESCRIPTION_COLUMN_WIDTH, '#fafafa', {
                fontWeight: 700,
              })}
            >
              Grand Total
            </TableCell>
            <TableCell
              sx={getLeftStickyCellSx(DESCRIPTION_COLUMN_WIDTH, UNIT_COLUMN_WIDTH, '#fafafa')}
            />
            {customerColumns.map((customer) => (
              <TableCell
                key={customer.customerNumber}
                sx={{
                  backgroundColor: '#fafafa',
                  width: CUSTOMER_COLUMN_WIDTH,
                  minWidth: CUSTOMER_COLUMN_WIDTH,
                  maxWidth: CUSTOMER_COLUMN_WIDTH,
                }}
              />
            ))}
            <TableCell
              sx={getRightStickyCellSx(
                REVENUE_COLUMN_WIDTH,
                TOTAL_QUANTITY_COLUMN_WIDTH,
                '#fafafa'
              )}
            />
            <TableCell
              align="center"
              sx={getRightStickyCellSx(0, REVENUE_COLUMN_WIDTH, '#fafafa', {
                fontWeight: 700,
              })}
            >
              {grandTotalSalesDisplay}
            </TableCell>
          </TableRow>
        </TableFooter>
      </Table>
    </TableContainer>
  );
});

export default function CustomerOrderMatrixPage({
  connected,
  loadingConnection,
  setConnected,
  setError,
}) {
  const [periodMode, setPeriodMode] = useState('month');
  const [selectedMonth, setSelectedMonth] = useState(() => dayjs().startOf('month'));
  const [customStartDate, setCustomStartDate] = useState(null);
  const [customEndDate, setCustomEndDate] = useState(null);
  const [customerOptions, setCustomerOptions] = useState([]);
  const [customerOptionsLoading, setCustomerOptionsLoading] = useState(false);
  const [matrixCustomers, setMatrixCustomers] = useState([]);
  const [customerPickerOpen, setCustomerPickerOpen] = useState(false);
  const [closeCustomerPickerAfterMatrixUpdate, setCloseCustomerPickerAfterMatrixUpdate] =
    useState(false);
  const [customerSearchValue, setCustomerSearchValue] = useState('');
  const [reportData, setReportData] = useState(null);
  const [, setReportStatus] = useState(null);
  const [reportSignature, setReportSignature] = useState('');
  const [preparing, setPreparing] = useState(false);
  const [polling, setPolling] = useState(false);
  const [pageMessage, setPageMessage] = useState(null);
  const currentSelectionSignatureRef = useRef('');
  const matrixCustomersRef = useRef(matrixCustomers);
  const customerOptionsLoadedRef = useRef(false);
  const customerOptionsLoadingRef = useRef(false);
  const prepareAbortRef = useRef(null);
  const statusAbortRef = useRef(null);
  const resultAbortRef = useRef(null);
  const customerOptionsAbortRef = useRef(null);
  const customerOptionsTimeoutRef = useRef(null);
  const pollingTimeoutRef = useRef(null);
  const isMountedRef = useRef(true);
  const syncMatrixToSelectionRef = useRef(null);

  const clearCustomerOptionsTimeout = useCallback(() => {
    if (customerOptionsTimeoutRef.current) {
      clearTimeout(customerOptionsTimeoutRef.current);
      customerOptionsTimeoutRef.current = null;
    }
  }, []);

  useEffect(() => {
    isMountedRef.current = true;

    return () => {
      isMountedRef.current = false;
      if (pollingTimeoutRef.current) {
        clearTimeout(pollingTimeoutRef.current);
      }
      prepareAbortRef.current?.abort();
      statusAbortRef.current?.abort();
      resultAbortRef.current?.abort();
      if (customerOptionsTimeoutRef.current) {
        clearTimeout(customerOptionsTimeoutRef.current);
        customerOptionsTimeoutRef.current = null;
      }
      customerOptionsAbortRef.current?.abort();
      customerOptionsAbortRef.current = null;
      customerOptionsLoadingRef.current = false;
    };
  }, []);

  useEffect(() => {
    if (loadingConnection || !connected) {
      setCustomerPickerOpen(false);
      setCloseCustomerPickerAfterMatrixUpdate(false);
      if (customerOptionsTimeoutRef.current) {
        clearTimeout(customerOptionsTimeoutRef.current);
        customerOptionsTimeoutRef.current = null;
      }
      customerOptionsAbortRef.current?.abort();
      customerOptionsAbortRef.current = null;
      setCustomerOptionsLoading(false);
      customerOptionsLoadingRef.current = false;
      if (!connected) {
        setCustomerSearchValue('');
        setCustomerOptions([]);
        customerOptionsLoadedRef.current = false;
        setMatrixCustomers([]);
      }
    }
  }, [connected, loadingConnection]);

  const selectedMonthValue = selectedMonth ? selectedMonth.format('YYYY-MM') : '';
  const selectedStartDateValue = customStartDate ? customStartDate.format('YYYY-MM-DD') : '';
  const selectedEndDateValue = customEndDate ? customEndDate.format('YYYY-MM-DD') : '';
  matrixCustomersRef.current = matrixCustomers;
  const matrixCustomerNumbers = useMemo(
    () => matrixCustomers.map((customer) => customer.customerNumber),
    [matrixCustomers]
  );
  const selectedPeriod = useMemo(
    () =>
      buildPeriodSelection({
        mode: periodMode,
        month: selectedMonthValue,
        startDate: selectedStartDateValue,
        endDate: selectedEndDateValue,
      }),
    [periodMode, selectedEndDateValue, selectedMonthValue, selectedStartDateValue]
  );
  const currentSelectionSignature = useMemo(
    () => buildRequestSignature(selectedPeriod, matrixCustomerNumbers),
    [matrixCustomerNumbers, selectedPeriod]
  );

  useEffect(() => {
    currentSelectionSignatureRef.current = currentSelectionSignature;
  }, [currentSelectionSignature]);

  useEffect(() => {
    if (!closeCustomerPickerAfterMatrixUpdate) {
      return;
    }

    setCustomerPickerOpen(false);
    setCloseCustomerPickerAfterMatrixUpdate(false);
  }, [closeCustomerPickerAfterMatrixUpdate, matrixCustomers]);

  const activeReportData =
    reportSignature && reportSignature === currentSelectionSignature ? reportData : null;
  const activeReportPeriod = useMemo(
    () => getReportPeriod(activeReportData),
    [activeReportData]
  );

  const tableViewModel = useMemo(() => {
    const customerColumns = matrixCustomers;

    const rows = (Array.isArray(activeReportData?.rows) ? activeReportData.rows : []).map(
      (row, index) => {
      const articleNumber = String(row?.articleNumber ?? '').trim();
      const articleDescription = String(row?.articleDescription ?? '').trim();
      const unit = String(row?.unit ?? '').trim();
      const customerQuantities =
        row?.customerQuantities && typeof row.customerQuantities === 'object'
          ? row.customerQuantities
          : {};
      const customerCells = customerColumns.map((customer) => {
        const numericValue = toFiniteNumber(customerQuantities[customer.customerNumber]);
        return {
          customerNumber: customer.customerNumber,
          numericValue,
          displayValue: formatQuantityValue(numericValue),
        };
      });
      const totalQuantityValue = toFiniteNumber(row?.totalQuantity);
      const totalSalesValue = toFiniteNumber(row?.totalSales);

      return {
        key: articleNumber || `${articleDescription || 'article'}-${index}`,
        articleNumberDisplay: articleNumber || '-',
        articleDescriptionDisplay: articleDescription || articleNumber || 'Unnamed article',
        unitDisplay: unit || '-',
        customerCells,
        totalQuantityValue,
        totalQuantityDisplay: formatQuantityValue(totalQuantityValue),
        totalSalesValue,
        totalSalesDisplay: formatRevenueValue(totalSalesValue),
      };
      }
    );

    const grandTotalSalesValue = toFiniteNumber(activeReportData?.grandTotalSales);
    const exportTitle = getExportTitle(
      activeReportPeriod.month || activeReportPeriod.startDate
        ? activeReportPeriod
        : selectedPeriod
    );
    const headerRow = [
      'Benämning',
      'Enhet',
      ...customerColumns.map((customer) => customer.customerName),
      'TOTAL ST/KG',
      'Omsättning',
    ];
    const dataRows = rows.map((row) => [
      row.articleDescriptionDisplay,
      row.unitDisplay === '-' ? '' : row.unitDisplay,
      ...row.customerCells.map((cell) => cell.numericValue),
      row.totalQuantityValue,
      row.totalSalesValue,
    ]);
    const footerRow = [
      'Grand Total',
      '',
      ...customerColumns.map(() => ''),
      '',
      grandTotalSalesValue,
    ];

    return {
      customerColumns,
      rows,
      grandTotalSalesDisplay: formatRevenueValue(grandTotalSalesValue),
      grandTotalSalesValue,
      exportRows: [[exportTitle], headerRow, ...dataRows, footerRow],
    };
  }, [activeReportData, activeReportPeriod, matrixCustomers, selectedPeriod]);

  const hasLoadedReport = Boolean(activeReportData);
  const hasSelectedCustomers = matrixCustomers.length > 0;
  const hasActiveRequest = preparing || polling;
  const actionsDisabled =
    !connected || loadingConnection || customerOptionsLoading || hasActiveRequest;
  const exportDisabled = actionsDisabled || !hasLoadedReport;
  const hasValidCustomRange = useMemo(
    () =>
      Boolean(
        customStartDate &&
          customEndDate &&
          !customStartDate.isAfter(customEndDate, 'day')
      ),
    [customEndDate, customStartDate]
  );
  const hasReadyPeriodSelection =
    periodMode === 'month'
      ? Boolean(selectedMonthValue)
      : Boolean(selectedStartDateValue && selectedEndDateValue && hasValidCustomRange);
  const customRangeLength = useMemo(
    () => getInclusiveRangeLength(customStartDate, customEndDate),
    [customEndDate, customStartDate]
  );
  const showCustomRangeWarning =
    periodMode === 'custom' &&
    Boolean(selectedStartDateValue && selectedEndDateValue && customRangeLength > 31);
  const selectedCustomerNumberSet = useMemo(
    () => new Set(matrixCustomers.map((customer) => customer.customerNumber)),
    [matrixCustomers]
  );
  const normalizedCustomerSearchValue = normalizeSearchValue(customerSearchValue);
  const customerSearchIndex = useMemo(
    () =>
      customerOptions.map((customer) => ({
        customer,
        customerNameSearch: normalizeSearchValue(customer.customerName),
        customerNumberSearch: normalizeSearchValue(customer.customerNumber),
      })),
    [customerOptions]
  );
  const searchedCustomerOptions = useMemo(() => {
    if (!normalizedCustomerSearchValue) {
      return [];
    }

    const matches = [];

    for (const entry of customerSearchIndex) {
      if (
        entry.customerNameSearch.includes(normalizedCustomerSearchValue) ||
        entry.customerNumberSearch.includes(normalizedCustomerSearchValue)
      ) {
        matches.push(entry.customer);
      }
    }

    return matches;
  }, [customerSearchIndex, normalizedCustomerSearchValue]);
  const matchingCustomers = useMemo(
    () =>
      searchedCustomerOptions.filter(
        (customer) => !selectedCustomerNumberSet.has(customer.customerNumber)
      ),
    [searchedCustomerOptions, selectedCustomerNumberSet]
  );
  const visibleCustomerOptions = useMemo(() => {
    if (!normalizedCustomerSearchValue) {
      const visibleOptions = [];
      const seenCustomerNumbers = new Set();

      matrixCustomers.forEach((customer) => {
        if (seenCustomerNumbers.has(customer.customerNumber)) {
          return;
        }

        seenCustomerNumbers.add(customer.customerNumber);
        visibleOptions.push(customer);
      });

      for (const customer of customerOptions) {
        if (seenCustomerNumbers.has(customer.customerNumber)) {
          continue;
        }

        seenCustomerNumbers.add(customer.customerNumber);
        visibleOptions.push(customer);

        if (visibleOptions.length >= MAX_VISIBLE_CUSTOMER_OPTIONS) {
          break;
        }
      }

      return visibleOptions;
    }
    return searchedCustomerOptions.slice(0, MAX_VISIBLE_CUSTOMER_OPTIONS);
  }, [customerOptions, matrixCustomers, normalizedCustomerSearchValue, searchedCustomerOptions]);
  const customerPickerHelperText = normalizedCustomerSearchValue
    ? searchedCustomerOptions.length > MAX_VISIBLE_CUSTOMER_OPTIONS
      ? `Showing the first ${MAX_VISIBLE_CUSTOMER_OPTIONS} of ${searchedCustomerOptions.length} matches. Use "Add all matches" to select them all.`
      : 'Search by restaurant name or customer number'
    : customerOptions.length > MAX_VISIBLE_CUSTOMER_OPTIONS
      ? `Showing the first ${MAX_VISIBLE_CUSTOMER_OPTIONS} customers. Type to narrow the list.`
      : 'Search by restaurant name or customer number';
  const customerPickerNoOptionsText = normalizedCustomerSearchValue
    ? 'No customers match the current search'
    : 'No customers available';

  const clearPollingLoop = () => {
    if (pollingTimeoutRef.current) {
      clearTimeout(pollingTimeoutRef.current);
      pollingTimeoutRef.current = null;
    }
  };

  const abortPrepareRequest = () => {
    if (prepareAbortRef.current) {
      prepareAbortRef.current.abort();
      prepareAbortRef.current = null;
    }
  };

  const abortResultRequest = () => {
    if (resultAbortRef.current) {
      resultAbortRef.current.abort();
      resultAbortRef.current = null;
    }
  };

  const abortStatusRequest = () => {
    if (statusAbortRef.current) {
      statusAbortRef.current.abort();
      statusAbortRef.current = null;
    }
  };

  const isCurrentSignature = (signature) =>
    isMountedRef.current && currentSelectionSignatureRef.current === signature;

  const clearMatrixRequestState = () => {
    clearPollingLoop();
    abortPrepareRequest();
    abortStatusRequest();
    abortResultRequest();
    setPreparing(false);
    setPolling(false);
  };

  const clearDisplayedMatrix = () => {
    clearMatrixRequestState();
    setReportData(null);
    setReportStatus(null);
    setReportSignature('');
  };

  const handleUnexpectedRequestError = (error, phase) => {
    if (isAbortError(error)) {
      return;
    }

    if (error.status === 401) {
      setConnected(false);
    }

    clearMatrixRequestState();
    setPageMessage({
      severity: 'error',
      message: getSafeMatrixErrorMessage(phase),
    });
  };

  const loadCustomerOptions = useCallback(async ({ force = false } = {}) => {
    if (loadingConnection || !connected) {
      return;
    }

    if (customerOptionsLoadingRef.current || customerOptionsAbortRef.current) {
      return;
    }

    if (customerOptionsLoadedRef.current && !force) {
      return;
    }

    clearCustomerOptionsTimeout();

    const abortController = new AbortController();
    customerOptionsAbortRef.current = abortController;
    customerOptionsLoadingRef.current = true;
    setCustomerOptionsLoading(true);
    setPageMessage((currentMessage) =>
      currentMessage?.source === 'customerOptions' ? null : currentMessage
    );

    customerOptionsTimeoutRef.current = window.setTimeout(() => {
      if (!isMountedRef.current || customerOptionsAbortRef.current !== abortController) {
        return;
      }

      customerOptionsAbortRef.current = null;
      clearCustomerOptionsTimeout();
      abortController.abort();
      customerOptionsLoadingRef.current = false;
      setCustomerOptionsLoading(false);
      setPageMessage({
        severity: 'error',
        message: CUSTOMER_OPTIONS_TIMEOUT_MESSAGE,
        source: 'customerOptions',
      });
    }, CUSTOMER_OPTIONS_TIMEOUT_MS);

    try {
      const options = await fetchCustomerOptions({ signal: abortController.signal });
      const normalizedOptions = normalizeCustomerSelections(options);

      if (!isMountedRef.current || customerOptionsAbortRef.current !== abortController) {
        return;
      }

      customerOptionsAbortRef.current = null;
      clearCustomerOptionsTimeout();
      setCustomerOptions(normalizedOptions);
      customerOptionsLoadedRef.current = normalizedOptions.length > 0;
      customerOptionsLoadingRef.current = false;
      setCustomerOptionsLoading(false);
      setConnected(true);
      setPageMessage((currentMessage) =>
        currentMessage?.source === 'customerOptions' ? null : currentMessage
      );
    } catch (error) {
      const isCurrentRequest = customerOptionsAbortRef.current === abortController;
      if (isCurrentRequest) {
        customerOptionsAbortRef.current = null;
        clearCustomerOptionsTimeout();
        if (isMountedRef.current) {
          customerOptionsLoadingRef.current = false;
          setCustomerOptionsLoading(false);
        }
      }

      if (!isMountedRef.current || isAbortError(error)) {
        return;
      }

      if (error.status === 401) {
        setConnected(false);
      }

      setPageMessage({
        severity: 'error',
        message: getSafeMatrixErrorMessage('customerOptions'),
        source: 'customerOptions',
      });
    }
  }, [
    clearCustomerOptionsTimeout,
    connected,
    loadingConnection,
    setConnected,
  ]);

  useEffect(() => {
    loadCustomerOptions();
  }, [loadCustomerOptions]);

  const fetchMatrixResult = async (signature, period, customerNumbers) => {
    abortResultRequest();
    const abortController = new AbortController();
    resultAbortRef.current = abortController;

    try {
      const response = await getCustomerOrderMatrixResult({
        month: period.month,
        startDate: period.startDate,
        endDate: period.endDate,
        customerNumbers,
        signal: abortController.signal,
      });

      if (resultAbortRef.current === abortController) {
        resultAbortRef.current = null;
      }

      if (!isCurrentSignature(signature)) {
        return;
      }

      if (response.status === 200) {
        setReportSignature(signature);
        setReportData(response.data);
        setReportStatus({
          state: 'READY',
          month: response.data?.month || period.month || null,
          startDate: response.data?.startDate || period.startDate || null,
          endDate: response.data?.endDate || period.endDate || null,
          customerNumbers,
          resultAvailable: true,
          generatedAt: response.data?.generatedAt || null,
        });
        setPageMessage(null);
        setPreparing(false);
        setPolling(false);
        clearPollingLoop();
        setConnected(true);
        return;
      }

      if (response.status === 202) {
        setReportSignature(signature);
        setReportStatus(response.data);
        setPreparing(false);
        setPolling(true);
        clearPollingLoop();
        pollingTimeoutRef.current = window.setTimeout(() => {
          pollMatrixStatus(signature, period, customerNumbers);
        }, POLL_INTERVAL_MS);
        return;
      }

      if (response.status === 404) {
        setReportSignature(signature);
        setReportData(null);
        setReportStatus(response.data);
        setPageMessage({
          severity: 'warning',
          message: EXPIRED_RESULT_MESSAGE,
        });
        setPreparing(false);
        setPolling(false);
        clearPollingLoop();
        return;
      }

      if (response.status === 409) {
        setReportSignature(signature);
        setReportData(null);
        setReportStatus(response.data);
        setPageMessage({
          severity: 'error',
          message: getSafeMatrixErrorMessage('generation'),
        });
        setPreparing(false);
        setPolling(false);
        clearPollingLoop();
      }
    } catch (error) {
      if (resultAbortRef.current === abortController) {
        resultAbortRef.current = null;
      }

      if (!isCurrentSignature(signature)) {
        return;
      }

      handleUnexpectedRequestError(error, 'result');
    }
  };

  const pollMatrixStatus = async (signature, period, customerNumbers) => {
    if (!isCurrentSignature(signature)) {
      return;
    }

    abortStatusRequest();
    const abortController = new AbortController();
    statusAbortRef.current = abortController;

    try {
      const response = await getCustomerOrderMatrixStatus({
        month: period.month,
        startDate: period.startDate,
        endDate: period.endDate,
        customerNumbers,
        signal: abortController.signal,
      });

      if (statusAbortRef.current === abortController) {
        statusAbortRef.current = null;
      }

      if (!isCurrentSignature(signature)) {
        return;
      }

      setReportSignature(signature);
      setReportStatus(response.data);

      if (response.data?.state === 'READY') {
        await fetchMatrixResult(signature, period, customerNumbers);
        return;
      }

      if (response.data?.state === 'FAILED') {
        clearPollingLoop();
        setPreparing(false);
        setPolling(false);
        setPageMessage({
          severity: 'error',
          message: getSafeMatrixErrorMessage('generation'),
        });
        return;
      }

      clearPollingLoop();
      pollingTimeoutRef.current = window.setTimeout(() => {
        pollMatrixStatus(signature, period, customerNumbers);
      }, POLL_INTERVAL_MS);
    } catch (error) {
      if (statusAbortRef.current === abortController) {
        statusAbortRef.current = null;
      }

      if (!isCurrentSignature(signature)) {
        return;
      }

      if (isAbortError(error)) {
        return;
      }

      handleUnexpectedRequestError(error, 'status');
    }
  };

  const startMatrixRefresh = async ({ period, customerNumbers }) => {
    const requestSignature = buildRequestSignature(period, customerNumbers);
    if (!requestSignature) {
      return;
    }

    clearMatrixRequestState();
    setPreparing(true);
    setPolling(false);
    setReportData(null);
    setReportStatus(null);
    setReportSignature('');
    setPageMessage(null);

    const abortController = new AbortController();
    prepareAbortRef.current = abortController;

    try {
      const response = await prepareCustomerOrderMatrix({
        month: period.month,
        startDate: period.startDate,
        endDate: period.endDate,
        customerNumbers,
        signal: abortController.signal,
      });

      if (prepareAbortRef.current === abortController) {
        prepareAbortRef.current = null;
      }

      if (!isCurrentSignature(requestSignature)) {
        return;
      }

      setReportSignature(requestSignature);
      setReportStatus(response.data);

      if (response.data?.state === 'READY') {
        await fetchMatrixResult(requestSignature, period, customerNumbers);
        return;
      }

      setPreparing(false);
      setPolling(true);
      clearPollingLoop();
      pollingTimeoutRef.current = window.setTimeout(() => {
        pollMatrixStatus(requestSignature, period, customerNumbers);
      }, POLL_INTERVAL_MS);
    } catch (error) {
      if (prepareAbortRef.current === abortController) {
        prepareAbortRef.current = null;
      }

      if (!isCurrentSignature(requestSignature)) {
        return;
      }

      handleUnexpectedRequestError(error, 'prepare');
    }
  };

  const handleGenerate = async () => {
    setError(null);

    if (!connected || loadingConnection) {
      setPageMessage({
        severity: 'warning',
        message: 'Connect to Fortnox before generating the customer report.',
      });
      return;
    }

    if (matrixCustomers.length === 0) {
      setPageMessage({
        severity: 'error',
        message: 'Please select at least one customer.',
      });
      return;
    }

    if (periodMode === 'month') {
      const normalizedMonth = normalizeMonthValue(selectedMonth);
      if (!normalizedMonth) {
        setPageMessage({
          severity: 'error',
          message: 'Please choose a month.',
        });
        return;
      }

      await startMatrixRefresh({
        period: buildPeriodSelection({
          mode: 'month',
          month: normalizedMonth.format('YYYY-MM'),
        }),
        customerNumbers: matrixCustomerNumbers,
      });
      return;
    }

    const normalizedStartDate = normalizeDateValue(customStartDate);
    if (!normalizedStartDate) {
      setPageMessage({
        severity: 'error',
        message: 'Please choose a start date.',
      });
      return;
    }

    const normalizedEndDate = normalizeDateValue(customEndDate);
    if (!normalizedEndDate) {
      setPageMessage({
        severity: 'error',
        message: 'Please choose an end date.',
      });
      return;
    }

    if (normalizedStartDate.isAfter(normalizedEndDate, 'day')) {
      setPageMessage({
        severity: 'error',
        message: 'Start date must be on or before end date.',
      });
      return;
    }

    await startMatrixRefresh({
      period: buildPeriodSelection({
        mode: 'custom',
        startDate: normalizedStartDate.format('YYYY-MM-DD'),
        endDate: normalizedEndDate.format('YYYY-MM-DD'),
      }),
      customerNumbers: matrixCustomerNumbers,
    });
  };

  const handleExport = () => {
    if (!hasLoadedReport) {
      setPageMessage({
        severity: 'error',
        message: 'Generate a report before exporting.',
      });
      return;
    }

    const worksheet = XLSX.utils.aoa_to_sheet(tableViewModel.exportRows) || {};
    worksheet['!cols'] = [
      { wpx: DESCRIPTION_COLUMN_WIDTH },
      { wpx: UNIT_COLUMN_WIDTH },
      ...tableViewModel.customerColumns.map(() => ({ wpx: CUSTOMER_COLUMN_WIDTH })),
      { wpx: TOTAL_QUANTITY_COLUMN_WIDTH },
      { wpx: REVENUE_COLUMN_WIDTH },
    ];

    const revenueColumnIndex = tableViewModel.customerColumns.length + 3;
    const firstRevenueRowIndex = 2;
    const lastRevenueRowIndex = tableViewModel.rows.length + 2;

    for (let rowIndex = firstRevenueRowIndex; rowIndex <= lastRevenueRowIndex; rowIndex += 1) {
      const cellAddress = XLSX.utils.encode_cell({
        r: rowIndex,
        c: revenueColumnIndex,
      });

      if (worksheet[cellAddress]) {
        worksheet[cellAddress].z = '#,##0.00 "kr"';
      }
    }

    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Customer Matrix');
    XLSX.writeFile(
      workbook,
      `customer_order_matrix_${getExportFilenameSuffix(
        activeReportPeriod.month || activeReportPeriod.startDate ? activeReportPeriod : selectedPeriod
      )}.xlsx`
    );
  };

  const syncMatrixToSelection = (nextCustomers, periodOverride = selectedPeriod) => {
    const normalizedCustomers = normalizeCustomerSelections(nextCustomers);
    const nextCustomerNumbers = normalizedCustomers.map((customer) => customer.customerNumber);
    const nextSignature = buildRequestSignature(periodOverride, nextCustomerNumbers);

    currentSelectionSignatureRef.current = nextSignature;
    clearDisplayedMatrix();
    setPageMessage(null);
  };
  syncMatrixToSelectionRef.current = syncMatrixToSelection;

  const handlePeriodModeChange = (_event, nextMode) => {
    if (!nextMode || nextMode === periodMode) {
      return;
    }

    setPeriodMode(nextMode);
    syncMatrixToSelection(
      matrixCustomers,
      buildPeriodSelection({
        mode: nextMode,
        month: selectedMonthValue,
        startDate: selectedStartDateValue,
        endDate: selectedEndDateValue,
      })
    );
  };

  const handleMonthChange = (event) => {
    const nextValue = event.target.value;
    const nextMonth = nextValue ? normalizeMonthValue(`${nextValue}-01`) : null;
    const nextMonthValue = nextMonth ? nextMonth.format('YYYY-MM') : '';

    setSelectedMonth(nextMonth);
    syncMatrixToSelection(
      matrixCustomers,
      buildPeriodSelection({
        mode: 'month',
        month: nextMonthValue,
      })
    );
  };

  const handleCustomStartDateChange = (event) => {
    const nextValue = event.target.value;
    const nextStartDate = nextValue ? normalizeDateValue(nextValue) : null;

    setCustomStartDate(nextStartDate);
    syncMatrixToSelection(
      matrixCustomers,
      buildPeriodSelection({
        mode: 'custom',
        startDate: nextStartDate ? nextStartDate.format('YYYY-MM-DD') : '',
        endDate: selectedEndDateValue,
      })
    );
  };

  const handleCustomEndDateChange = (event) => {
    const nextValue = event.target.value;
    const nextEndDate = nextValue ? normalizeDateValue(nextValue) : null;

    setCustomEndDate(nextEndDate);
    syncMatrixToSelection(
      matrixCustomers,
      buildPeriodSelection({
        mode: 'custom',
        startDate: selectedStartDateValue,
        endDate: nextEndDate ? nextEndDate.format('YYYY-MM-DD') : '',
      })
    );
  };

  const handleCustomerChange = (_event, value) => {
    const normalizedCustomers = normalizeCustomerSelections(value);

    setMatrixCustomers(normalizedCustomers);
    syncMatrixToSelection(normalizedCustomers);
  };

  const handleClearCustomers = () => {
    syncMatrixToSelection([]);
    setCustomerSearchValue('');
    setMatrixCustomers([]);
  };

  const handleRemoveCustomer = useCallback((customerNumber) => {
    const nextCustomers = matrixCustomersRef.current.filter(
      (customer) => customer.customerNumber !== customerNumber
    );
    syncMatrixToSelectionRef.current?.(nextCustomers);
    setCustomerSearchValue('');
    setMatrixCustomers(nextCustomers);
  }, []);

  const handleAddMatchingCustomers = () => {
    if (matchingCustomers.length === 0) {
      return;
    }

    const nextCustomers = normalizeCustomerSelections([
      ...matrixCustomers,
      ...matchingCustomers,
    ]);
    setCustomerSearchValue('');
    setCloseCustomerPickerAfterMatrixUpdate(true);
    setMatrixCustomers(nextCustomers);
    syncMatrixToSelection(nextCustomers);
  };

  return (
    <ThemeProvider theme={CUSTOMER_MATRIX_THEME}>
      <Box
        sx={{
          minHeight: '100vh',
          background: PAGE_BACKGROUND,
          py: { xs: 3, md: 4 },
          px: { xs: 2, sm: 3 },
        }}
      >
        <Container maxWidth="xl">
          <Stack spacing={2.25}>
            <Paper elevation={0} sx={{ ...SURFACE_SX, p: { xs: 1.5, md: 2 } }}>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                }}
              >
                <Stack direction="row" spacing={1.25} alignItems="center">
                  <Avatar
                    sx={{
                      width: 48,
                      height: 48,
                      bgcolor: 'rgba(84,110,122,0.14)',
                      color: pageColors.customerOrderMatrix,
                    }}
                  >
                    <ViewWeekIcon />
                  </Avatar>

                  <Box sx={{ minWidth: 0 }}>
                    <Typography
                      variant="overline"
                      sx={{ color: pageColors.customerOrderMatrix, letterSpacing: 3 }}
                    >
                      CUSTOMER REPORT
                    </Typography>
                    <Typography variant="h4" fontWeight={700} sx={{ overflowWrap: 'anywhere' }}>
                      Customer Report
                    </Typography>
                    <Typography variant="body1" color="text.secondary" sx={{ mt: 0.25 }}>
                      Generate a dense workbook-style report by customer for a month or custom
                      date range and export the same layout to Excel.
                    </Typography>
                  </Box>
                </Stack>
              </Box>
            </Paper>

            <Paper elevation={0} sx={{ ...SURFACE_SX, p: { xs: 1.5, md: 1.75 } }}>
              <Stack spacing={1.25}>
                <Box>
                  <Typography variant="h6" fontWeight={600}>
                    Filters
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Choose a month or custom date range, search customers by name or number,
                    then add the full filtered result in one step when needed.
                  </Typography>
                </Box>

                <Stack spacing={1.25}>
                  <Box
                    sx={{
                      display: 'flex',
                      flexWrap: 'wrap',
                      gap: 1.25,
                      alignItems: 'start',
                    }}
                  >
                    <ToggleButtonGroup
                      exclusive
                      value={periodMode}
                      onChange={handlePeriodModeChange}
                      size="small"
                      sx={{ flex: { sm: '0 0 auto' } }}
                    >
                      <ToggleButton value="month">Month</ToggleButton>
                      <ToggleButton value="custom">Custom range</ToggleButton>
                    </ToggleButtonGroup>

                    {periodMode === 'month' ? (
                      <TextField
                        label="Month"
                        type="month"
                        value={selectedMonthValue}
                        onChange={handleMonthChange}
                        size="small"
                        fullWidth
                        sx={{ ...MONTH_FILTER_FIELD_SX, ...PERIOD_INPUT_SX }}
                        InputLabelProps={{ shrink: true }}
                      />
                    ) : (
                      <>
                        <TextField
                          label="Start date"
                          type="date"
                          value={selectedStartDateValue}
                          onChange={handleCustomStartDateChange}
                          size="small"
                          fullWidth
                          sx={{ ...DATE_FILTER_FIELD_SX, ...PERIOD_INPUT_SX }}
                          InputLabelProps={{ shrink: true }}
                        />

                        <TextField
                          label="End date"
                          type="date"
                          value={selectedEndDateValue}
                          onChange={handleCustomEndDateChange}
                          size="small"
                          fullWidth
                          sx={{ ...DATE_FILTER_FIELD_SX, ...PERIOD_INPUT_SX }}
                          InputLabelProps={{ shrink: true }}
                        />
                      </>
                    )}
                  </Box>

                  {showCustomRangeWarning ? (
                    <Alert severity="warning" sx={{ borderRadius: 3 }}>
                      {CUSTOM_RANGE_WARNING_MESSAGE}
                    </Alert>
                  ) : null}

                  <Box
                    sx={{
                      display: 'flex',
                      flexWrap: 'wrap',
                      gap: 1.25,
                      alignItems: 'start',
                    }}
                  >
                    <Box sx={CUSTOMER_FILTER_FIELD_SX}>
                      <Autocomplete
                        multiple
                        disableCloseOnSelect
                        disablePortal
                        sx={{ width: '100%' }}
                        open={customerPickerOpen}
                        openOnFocus
                        onOpen={() => {
                          setCustomerPickerOpen(true);
                          loadCustomerOptions({
                            force:
                              !customerOptionsLoading &&
                              (
                                pageMessage?.source === 'customerOptions' ||
                                customerOptions.length === 0
                              ),
                          });
                        }}
                        onClose={() => {
                          setCustomerPickerOpen(false);
                        }}
                        options={visibleCustomerOptions}
                        filterOptions={(options) => options}
                        value={matrixCustomers}
                        inputValue={customerSearchValue}
                        onChange={handleCustomerChange}
                        onInputChange={(_event, value, reason) => {
                          if (reason === 'blur') {
                            return;
                          }

                          setCustomerSearchValue(value);
                        }}
                        loading={customerOptionsLoading}
                        isOptionEqualToValue={(option, value) =>
                          option.customerNumber === value.customerNumber
                        }
                        getOptionLabel={(option) =>
                          `${option.customerName} (${option.customerNumber})`
                        }
                        renderTags={() => []}
                        renderOption={(props, option, { selected }) => (
                          <Box component="li" {...props} key={option.customerNumber}>
                            <Checkbox
                              checked={selected}
                              size="small"
                              tabIndex={-1}
                              disableRipple
                              sx={{ mr: 1, p: 0.5 }}
                            />
                            <Box sx={{ minWidth: 0 }}>
                              <Typography variant="body2" fontWeight={600}>
                                {option.customerName}
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                {option.customerNumber}
                              </Typography>
                            </Box>
                          </Box>
                        )}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            label="Add customers"
                            size="small"
                            helperText={customerPickerHelperText}
                            placeholder="Search by customer name or number"
                          />
                        )}
                        loadingText="Loading customers..."
                        noOptionsText={customerPickerNoOptionsText}
                      />
                    </Box>

                    <Stack
                      direction={{ xs: 'column', sm: 'row' }}
                      spacing={1}
                      sx={{
                        width: { xs: '100%', sm: 'auto' },
                        flex: { sm: '0 0 auto' },
                      }}
                    >
                      <Button
                        variant="outlined"
                        color="primary"
                        onMouseDown={preventDefaultMouseDown}
                        onClick={handleAddMatchingCustomers}
                        disabled={matchingCustomers.length === 0}
                        sx={{ minHeight: 38, minWidth: { sm: 156 } }}
                      >
                        Add all matches
                        {matchingCustomers.length > 0 ? ` (${matchingCustomers.length})` : ''}
                      </Button>
                      <Button
                        variant="outlined"
                        color="primary"
                        onClick={handleClearCustomers}
                        disabled={matrixCustomers.length === 0}
                        sx={{ minHeight: 38, minWidth: { sm: 136 } }}
                      >
                        Clear all
                      </Button>
                      <Button
                        variant="contained"
                        color="primary"
                        onClick={handleGenerate}
                        disabled={actionsDisabled}
                        sx={{ minHeight: 38, minWidth: { sm: 136 } }}
                      >
                        {preparing || polling ? 'Generating...' : 'Generate'}
                      </Button>
                      <Button
                        variant="outlined"
                        startIcon={<DownloadIcon />}
                        onClick={handleExport}
                        disabled={exportDisabled}
                        sx={{ minHeight: 38, minWidth: { sm: 136 } }}
                      >
                        Export
                      </Button>
                    </Stack>
                  </Box>
                </Stack>
              </Stack>
            </Paper>

            {hasActiveRequest && (
              <Paper elevation={0} sx={{ ...SURFACE_SX, p: 0, overflow: 'hidden' }}>
                <LinearProgress />
              </Paper>
            )}

            {pageMessage ? (
              <Alert severity={pageMessage.severity} sx={{ borderRadius: 3 }}>
                {pageMessage.message}
              </Alert>
            ) : null}

            {!pageMessage && !connected && !loadingConnection ? (
              <Alert severity="warning" sx={{ borderRadius: 3 }}>
                Connect to Fortnox to load customer options and generate the report.
              </Alert>
            ) : null}

            {!pageMessage && !hasLoadedReport && !hasActiveRequest && connected ? (
              <Alert severity="info" sx={{ borderRadius: 3 }}>
                {hasSelectedCustomers && hasReadyPeriodSelection
                  ? 'The selected customers define the report columns. Press Generate to load the report.'
                  : 'Choose a month or custom date range and at least one customer to populate the report.'}
              </Alert>
            ) : null}

            <CustomerOrderMatrixTable
              customerColumns={tableViewModel.customerColumns}
              rows={tableViewModel.rows}
              grandTotalSalesDisplay={tableViewModel.grandTotalSalesDisplay}
              hasLoadedReport={hasLoadedReport}
              hasSelectedCustomers={hasSelectedCustomers}
              hasReadyPeriodSelection={hasReadyPeriodSelection}
              isRefreshing={hasActiveRequest}
              onRemoveCustomer={handleRemoveCustomer}
            />
          </Stack>
        </Container>
      </Box>
    </ThemeProvider>
  );
}
